import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { Sphere, SatelliteSphere } from 'types/global';
import type { GlyphColor } from '../../../types/glyph';
import { getSphereById, fetchPermissionList } from '@center-platform/spheres/sphere';
import { globalSphereCache } from '@center-platform/spheres/cache';
import { useContextTree } from '../../../hooks/useContextTree';
import { ARMILLARY_ENRICHMENT } from '../../../utils/enrichmentPresets';
import { useAvailableContexts } from '../../../hooks/useAvailableContexts';
import OrbitalVisualizationMolecule, {
  SelectedNodeInfo,
  OrbitalVisualizationMoleculeRef,
  NodePosition
} from '../../molecules/OrbitalVisualization/OrbitalVisualizationMolecule';
import ControlPanelMolecule from '../../molecules/ControlPanel/ControlPanelMolecule';
import SelectedNodeHeaderMolecule from '../../molecules/SelectedNodeHeader/SelectedNodeHeaderMolecule';
import DialogWrapper from '../../molecules/DialogWrapper/DialogWrapper';
import EditSphere from '../../molecules/Atlas/EditSphere/EditSphere';
import AddSphere from '../../molecules/AddSphere/AddSphere';
import AddTemplate from '../../molecules/AddTemplate/AddTemplate';
import SphereAvatar from '../../molecules/sphere-avatar/sphere-avatar';
import SatelliteViewerMolecule from '../../molecules/SatelliteViewer/SatelliteViewerMolecule';
import LoaderMolecule from '../../molecules/Loader/LoaderMolecule';
import ContextSwitcherToolbarMolecule, { ContextSwitcherToolbarHandle } from '../../molecules/ContextSwitcherToolbar/ContextSwitcherToolbarMolecule';
import ContextViewEditorOverlayOrganism from '../ContextViewEditorOverlay/ContextViewEditorOverlayOrganism';
import { NodeData, ORBITS, ORBITS_LIGHT } from '../../molecules/OrbitalVisualization/constants';
import { assignSpheresToOrbits } from '../../molecules/OrbitalVisualization/orbitAssignment';
import { convertSphereNodesToNodeData, convertSpheresToNodeData } from './dataConverters';
import { convertNotificationsToNodeData, filterNotificationsForOrbit } from './notificationConverters';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useArmillaryContextOrNull } from '../../../context/ArmillaryContext';
import { useToolbarContextOrNull } from '../../../context/ToolbarContext';
import { useMetaspaceEvents } from '../../../hooks/useMetaspaceEvents';
import { isTransientReadFailure } from '../../../utils/isTransientReadFailure';
import './ArmillaryViewOrganism.scss';

/** Context system name for armillary view */
const ARMILLARY_CONTEXT = 'center_armillary_context';

interface BreadcrumbEntry {
  sphereId: string;
  name: string;
  glyph?: string;
  glyphColor?: GlyphColor;
  imageId?: string;
}

export interface ArmillaryViewOrganismProps {
  /** Sphere ID to load data for (required for live data) */
  sphereId?: string;
  className?: string;
  showControls?: boolean;
  centerImagePath?: string;
  /** Page limit for nearby spheres (default: 10) */
  pageLimit?: number;
  /** Callback when a node is selected */
  onNodeSelect?: (sphere: Sphere | null) => void;
  /** Enable velocity-based animation speeds (default: true) */
  useVelocitySpeed?: boolean;
  /** Callback when conversation icon is clicked (opens chat toolbar) */
  onConversationClick?: () => void;
  /** Navigate to the context editor for the given context system name. */
  onEditContext?: (contextSystemName: string) => void;
}

const ArmillaryViewOrganism: React.FC<ArmillaryViewOrganismProps> = ({
  sphereId,
  className = '',
  showControls = true,
  centerImagePath,
  pageLimit = 10,
  onNodeSelect,
  useVelocitySpeed = true,
  onConversationClick,
  onEditContext,
}) => {
  const { t } = useTranslation();

  // Performance configuration
  const MAX_SELECTED_ORBITS = 10;

  // Internal active sphere ID — overridden on double-click navigation
  const [activeSphereId, setActiveSphereId] = useState(sphereId);
  // Breadcrumb navigation trail (ancestors only — does NOT include the current sphere)
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbEntry[]>([]);
  // Locally-cached current sphere info for instant breadcrumb display (no fetch delay)
  const [activeSphereInfo, setActiveSphereInfo] = useState<BreadcrumbEntry | null>(null);

  // Context switching state
  const [activeContextSystemName, setActiveContextSystemName] = useState(ARMILLARY_CONTEXT);
  const [activeContextDisplayName, setActiveContextDisplayName] = useState(t('contextSelector.default'));
  const [isFadingOut, setIsFadingOut] = useState(false);
  // True once the user explicitly picks a context (popover) — the server-resolved
  // default stops auto-applying until the next external navigation.
  const manualContextRef = useRef(false);

  // Sync when prop changes (external navigation resets the stack).
  //
  // Keyed on sphereId only. `t` was in this dependency list, but its identity changes
  // whenever i18n swaps a language or merges a resource bundle (which happens twice
  // during boot) — each of those wiped the breadcrumb trail and re-showed the loader
  // even though the user had not navigated anywhere.
  useEffect(() => {
    setActiveSphereId(sphereId);
    setBreadcrumbs([]);
    setActiveSphereInfo(null);
    // Reset context to default on external navigation; the sphere's resolved
    // default (schema/arc-stage authored) re-applies when available-contexts returns.
    manualContextRef.current = false;
    setActiveContextSystemName(ARMILLARY_CONTEXT);
    setActiveContextDisplayName(t('contextSelector.default'));
    setIsFadingOut(false);
    // Reset loader on external navigation
    setShowLoader(true);
    setLoaderFadingOut(false);
  }, [sphereId]);

  // Detect active theme to pick the right orbital ring colours
  const [isLightTheme, setIsLightTheme] = useState(
    () => document.documentElement.dataset.theme !== 'dark'
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsLightTheme(document.documentElement.dataset.theme !== 'dark');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const activeOrbits = isLightTheme ? ORBITS_LIGHT : ORBITS;

  const [isAnimating, setIsAnimating] = useState(true);
  const [selectedOrbits, setSelectedOrbits] = useState<Set<number>>(new Set());
  const [isNodeSelected, setIsNodeSelected] = useState(false);
  const [nodePositions, setNodePositions] = useState<Map<number, NodePosition>>(new Map());
  const [showLoader, setShowLoader] = useState(true);
  const [loaderFadingOut, setLoaderFadingOut] = useState(false);
  const orbitalVisualizationRef = useRef<OrbitalVisualizationMoleculeRef>(null);
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const isProcessingClick = useRef(false);

  // Sphere editor dialog state
  const [editSphereDialogVisible, setEditSphereDialogVisible] = useState(false);
  const [sphereToEdit, setSphereToEdit] = useState<Sphere | null>(null);

  // Add sphere dialog state
  const [templateDialogVisible, setTemplateDialogVisible] = useState(false);
  const [addSphereDialogVisible, setAddSphereDialogVisible] = useState(false);
  const [selectedTemplateSchema, setSelectedTemplateSchema] = useState<any>(null);
  const [selectedSphereType, setSelectedSphereType] = useState<{ source: 'type' | 'usecase'; label: string } | null>(null);

  // Local sphere data state for adding new spheres
  const [localSphereData, setLocalSphereData] = useState<Sphere[]>([]);

  // Image URLs for spheres (fetched via globalSphereCache)
  const [sphereImageUrls, setSphereImageUrls] = useState<Map<string, string>>(new Map());

  // Satellite viewer state
  const [selectedNodeForSatellites, setSelectedNodeForSatellites] = useState<NodeData | null>(null);
  const [satelliteViewerVisible, setSatelliteViewerVisible] = useState(false);
  const [satelliteSourcePosition, setSatelliteSourcePosition] = useState<{ x: number; y: number } | undefined>(undefined);

  // Context switcher state (richer dropdown with views and editor)
  const contextSwitcherRef = useRef<ContextSwitcherToolbarHandle>(null);
  const [activeViewSystemName, setActiveViewSystemName] = useState<string | null>(null);
  const [switcherRefreshKey, setSwitcherRefreshKey] = useState(0);
  const [viewEditorState, setViewEditorState] = useState<{
    mode: 'new' | 'edit';
    viewSystemName?: string;
    contextSystemName: string;
    contextDisplayName: string;
  } | null>(null);

  // Get API client for surface fetching
  const apiClients = useApiClientsOrNull();
  const graphClient = apiClients?.graphClient;

  // Available contexts for the current sphere + the server-resolved default
  // (schema default_context / arc-stage overrides, viewer-perspective aware).
  const {
    contexts: availableContexts,
    defaultContext: resolvedDefaultContext,
    loading: availableContextsLoading,
  } = useAvailableContexts(apiClients, activeSphereId);

  // Apply the sphere's authored default view unless the user has manually
  // picked one. Fires when navigation lands on a sphere whose schema or
  // current arc stage names a default context; otherwise the component
  // default (ARMILLARY_CONTEXT) stays active.
  useEffect(() => {
    if (availableContextsLoading || manualContextRef.current) return;
    const target = resolvedDefaultContext?.contextSystemName ?? ARMILLARY_CONTEXT;
    setActiveContextSystemName((current) => {
      if (current === target) return current;
      const ctx = availableContexts.find((c) => c.system_name === target);
      const rawName = (ctx?.name ?? null) as { default?: string } | string | null;
      const resolvedName = typeof rawName === 'string' ? rawName : rawName?.default;
      const displayName =
        target === ARMILLARY_CONTEXT ? t('contextSelector.default') : resolvedName ?? target;
      setActiveContextDisplayName(displayName);
      return target;
    });
  }, [resolvedDefaultContext, availableContextsLoading, availableContexts, t]);

  // Get Armillary context for sharing orbital sphere IDs with ArmillaryActionsMolecule
  const armillaryContext = useArmillaryContextOrNull();

  // Metaspace event system for toolbar communication
  const { emitToolbarExpand, emitSphereCentered } = useMetaspaceEvents();

  // Get Toolbar context for notification data (moved from ArmillaryContext)
  const toolbarContext = useToolbarContextOrNull();

  // Fetch sphere data using the armillary context
  // Note: useContextTree will automatically fetch when sphereId changes
  // Only fetch when we have both sphereId and apiClients
  const {
    roots: contextRoots,
    loading: contextLoading,
    refresh: refreshContext,
    refreshQuietly: refreshContextQuietly,
  } = useContextTree(
    apiClients,
    {
      systemName: activeContextSystemName,
      rootSphereId: activeSphereId ?? '',
      limit: pageLimit,
      depth: 3, // 3 levels: orbitals -> satellites -> sub-satellites
      lazyLoad: false, // Eagerly load all levels for visualization
      enrichmentOverrides: ARMILLARY_ENRICHMENT,
    }
  );

  // Combined loading state
  const surfaceLoading = contextLoading;

  // Root SphereNode from context response (roots[0] is the centered sphere)
  const rootNode = useMemo(() => {
    return contextRoots && contextRoots.length > 0 ? contextRoots[0] : null;
  }, [contextRoots]);

  // Minimal Sphere for components that still require the legacy type (dial, AddSphere)
  const rootSphereCompat: Sphere | null = useMemo(() => {
    if (!rootNode) return null;
    return {
      id: rootNode.id,
      label: rootNode.label || 'sphere',
      glyph: rootNode.glyph,
      glyphColor: rootNode.glyphColor,
      properties: rootNode.properties as Sphere['properties'],
      status: rootNode.status,
      edgeCount: rootNode.edgeCount,
    };
  }, [rootNode]);

  // Keep activeSphereInfo in sync once the fetch completes
  useEffect(() => {
    if (rootNode && rootNode.id === activeSphereId) {
      setActiveSphereInfo({
        sphereId: rootNode.id,
        name: (rootNode.properties?.name as string) || 'Sphere',
        glyph: rootNode.glyph,
        glyphColor: rootNode.glyphColor,
        imageId: rootNode.properties?.imageId as string | undefined,
      });
    }
  }, [rootNode, activeSphereId]);

  // Convert orbital SphereNodes directly to NodeData (skip Sphere intermediary)
  const orbitalNodes = useMemo((): NodeData[] => {
    const serverNodes = rootNode?.children?.length
      ? convertSphereNodesToNodeData(rootNode.children)
      : [];
    // Merge in locally-added spheres (from AddSphere dialog)
    if (localSphereData.length > 0) {
      const localNodes = convertSpheresToNodeData(localSphereData);
      return [...localNodes, ...serverNodes];
    }
    return serverNodes;
  }, [rootNode, localSphereData]);

  // Orbital IDs for context (notifications, tasks, etc.)
  const orbitalIds = useMemo(() => orbitalNodes.map(n => n.id), [orbitalNodes]);

  // Publish orbital sphere IDs to context for notification batch fetching
  useEffect(() => {
    if (armillaryContext && orbitalIds.length > 0) {
      armillaryContext.setOrbitalSphereIds(orbitalIds);
    }
  }, [orbitalIds, armillaryContext]);

  // Publish root sphere ID and type to context + emit SPHERE_CENTERED
  useEffect(() => {
    if (armillaryContext && activeSphereId) {
      armillaryContext.setRootSphereId(activeSphereId);
    }
    if (activeSphereId) {
      const sphereType = rootNode
        ? (rootNode.properties?.type as string) || rootNode.label || undefined
        : undefined;
      emitSphereCentered(activeSphereId, sphereType);
    }
  }, [activeSphereId, armillaryContext, emitSphereCentered, rootNode]);

  // Publish root sphere type to context
  useEffect(() => {
    if (armillaryContext && rootNode) {
      const sphereType = (rootNode.properties?.type as string) || rootNode.label || null;
      armillaryContext.setRootSphereType(sphereType);
    }
  }, [rootNode, armillaryContext]);

  // Read every orbital sphere's picture (its avatar slot or legacy image) through the sphere cache.
  // The map is replaced on every read, so a removed picture goes away; a node whose read failed
  // transiently keeps the picture it showed, a refused read (403, 404, anything not transient) shows
  // none (spec D71), and a read overtaken by a newer one is dropped.
  useEffect(() => {
    if (orbitalNodes.length === 0 || !graphClient) return;
    let current = true;

    const readPictures = async () => {
      const reads = await Promise.all(
        orbitalNodes.map(async (node): Promise<[string, { url?: string } | null]> => {
          try {
            const cachedData = await globalSphereCache.getSphereData(node.id, graphClient);
            return [node.id, { url: cachedData.thumbnailUrl }];
          } catch (error) {
            console.warn(`[ArmillaryView] Failed to fetch image for sphere ${node.id}:`, error);
            return [node.id, isTransientReadFailure(error) ? null : {}];
          }
        })
      );
      if (!current) return;

      setSphereImageUrls((previous) => {
        const next = new Map<string, string>();
        for (const [id, read] of reads) {
          const url = read ? read.url : previous.get(id);
          if (url) next.set(id, url);
        }
        return next;
      });
    };

    void readPictures();
    return () => {
      current = false;
    };
  }, [orbitalNodes, graphClient]);

  // Assign NodeData to orbits based on gravity
  const orbitNodes = useMemo((): NodeData[][] => {
    // Get unread notifications from toolbar context
    const unreadNotifications = toolbarContext?.unreadNotifications || [];
    const filteredNotifications = filterNotificationsForOrbit(unreadNotifications, 10);

    // Convert notifications to NodeData
    const notificationNodes = convertNotificationsToNodeData(filteredNotifications);

    if (orbitalNodes.length > 0) {
      // Already NodeData[] — just merge in fetched image URLs
      let nodeData = orbitalNodes;

      // Merge in fetched image URLs
      if (sphereImageUrls.size > 0) {
        nodeData = nodeData.map(node => {
          const imageUrl = sphereImageUrls.get(node.id);
          if (imageUrl) {
            return { ...node, imagePath: imageUrl };
          }
          return node;
        });
      }

      // Combine sphere nodes with notification nodes
      const combinedNodes = [...nodeData, ...notificationNodes];

      // Assign to orbits based on gravity scores
      return assignSpheresToOrbits(combinedNodes, activeOrbits);
    }

    // If no orbital data, still show notifications on their orbit
    if (notificationNodes.length > 0) {
      return assignSpheresToOrbits(notificationNodes, activeOrbits);
    }

    return [];
  }, [orbitalNodes, sphereImageUrls, toolbarContext?.unreadNotifications, activeOrbits]);

  // Use orbit nodes directly
  const displayNodes = orbitNodes;

  // Manage loader visibility based on loading state and data availability
  useEffect(() => {
    if (surfaceLoading) {
      // Show loader while actively loading
      setShowLoader(true);
      setLoaderFadingOut(false);
    } else if (showLoader) {
      // Loading finished (with or without data) - start fade out
      setLoaderFadingOut(true);
      // Hide loader after fade-out animation completes (800ms)
      const hideTimer = setTimeout(() => {
        setShowLoader(false);
        setLoaderFadingOut(false);
      }, 800);
      return () => clearTimeout(hideTimer);
    }
  }, [surfaceLoading, displayNodes, showLoader]);

  const handlePlay = () => {
    setIsAnimating(true);
  };

  const handlePause = () => {
    setIsAnimating(false);
  };

  const handleStop = () => {
    setIsAnimating(false);
  };

  // Handle refresh - refetch data and reset orbital selections
  const handleRefresh = useCallback(() => {
    console.log('[ArmillaryView] Refresh clicked - refetching data');
    // Reset selections
    setSelectedOrbits(new Set());
    setIsNodeSelected(false);
    setNodePositions(new Map());
    // Restart animation
    setIsAnimating(true);
    // Refetch data
    refreshContext();
  }, [refreshContext]);

  // Handle context selection from context selector popover
  const handleContextSelect = useCallback((systemName: string, displayName: string) => {
    // Guard against selecting the already-active context
    if (systemName === activeContextSystemName) return;

    // Explicit user choice — stop auto-applying the resolved default until
    // the next external navigation.
    manualContextRef.current = true;

    // Fade out, switch context, then fade back in
    setIsFadingOut(true);
    setTimeout(() => {
      setActiveContextSystemName(systemName);
      setActiveContextDisplayName(
        systemName === ARMILLARY_CONTEXT ? t('contextSelector.default') : displayName
      );
      // Clear active view on context switch
      setActiveViewSystemName(null);
      // Reset orbital selections on context switch
      setSelectedOrbits(new Set());
      setIsNodeSelected(false);
      setNodePositions(new Map());
      setSatelliteViewerVisible(false);
      setSelectedNodeForSatellites(null);
      // Reset loader on context switch
      setShowLoader(true);
      setLoaderFadingOut(false);
      // Fade back in after state update
      setTimeout(() => {
        setIsFadingOut(false);
      }, 50);
    }, 300);
  }, [activeContextSystemName, t]);

  // Handle filter icon click — delegate to external ContextSwitcherToolbarMolecule
  const handleContextFilterClick = useCallback((event: MouseEvent) => {
    contextSwitcherRef.current?.open(event);
  }, []);

  // Handle view selection from context switcher
  const handleViewSelect = useCallback(
    (viewSystemName: string, _viewDisplayName: string, contextSystemName: string, _contextDisplayName: string) => {
      // Switch context if different
      if (contextSystemName !== activeContextSystemName) {
        setActiveContextSystemName(contextSystemName);
      }
      setActiveViewSystemName(viewSystemName);
      // Reset orbital state on view switch
      setSelectedOrbits(new Set());
      setIsNodeSelected(false);
      setNodePositions(new Map());
      setSatelliteViewerVisible(false);
      setSelectedNodeForSatellites(null);
      // Show loader for data refresh
      setShowLoader(true);
      setLoaderFadingOut(false);
    },
    [activeContextSystemName]
  );

  // Handle new view creation
  const handleNewView = useCallback((contextSystemName: string, contextDisplayName: string) => {
    setViewEditorState({ mode: 'new', contextSystemName, contextDisplayName });
  }, []);

  // Handle edit view
  const handleEditView = useCallback((viewSystemName: string, contextSystemName: string) => {
    setViewEditorState({ mode: 'edit', viewSystemName, contextSystemName, contextDisplayName: activeContextDisplayName });
  }, [activeContextDisplayName]);

  // Handle view saved
  const handleViewSaved = useCallback((savedViewSystemName: string) => {
    setActiveViewSystemName(savedViewSystemName);
    setSwitcherRefreshKey((k) => k + 1);
    setViewEditorState(null);
  }, []);

  // Handle view deleted
  const handleViewDeleted = useCallback((deletedViewSystemName: string) => {
    if (deletedViewSystemName === activeViewSystemName) {
      setActiveViewSystemName(null);
    }
    setSwitcherRefreshKey((k) => k + 1);
    setViewEditorState(null);
  }, [activeViewSystemName]);

  // Handle opening sphere editor
  const handleOpenSphereEditor = useCallback(async () => {
    console.log('[ArmillaryView] Edit icon clicked, activeSphereId:', activeSphereId);
    if (!activeSphereId || !graphClient) {
      console.warn('[ArmillaryView] Cannot open editor - no activeSphereId or graphClient');
      return;
    }

    try {
      // Fetch the full sphere data
      const response = await getSphereById(graphClient, activeSphereId, true, true);
      if (response.success && response.data?.sphere) {
        console.log('[ArmillaryView] Sphere fetched for editing:', response.data.sphere);
        setSphereToEdit(response.data.sphere);
        setEditSphereDialogVisible(true);
      } else {
        console.error('[ArmillaryView] Failed to fetch sphere:', response);
      }
    } catch (error) {
      console.error('[ArmillaryView] Error fetching sphere for editing:', error);
    }
  }, [activeSphereId, graphClient]);

  // Handle closing sphere editor
  const handleCloseSphereEditor = useCallback(() => {
    setEditSphereDialogVisible(false);
    setSphereToEdit(null);
  }, []);

  // Handle sphere save
  const handleSaveSphere = useCallback((updatedSphere: Sphere) => {
    console.log('[ArmillaryView] Sphere saved:', updatedSphere);
    handleCloseSphereEditor();
    // Refresh data to reflect changes
    refreshContext();
  }, [handleCloseSphereEditor, refreshContext]);

  // The sphere changed while its editor stays open (its glyph, colour or picture): re-read the tree in
  // the background, without the full-surface loader, keeping the orbits on screen if the read fails.
  const handleSphereRefresh = useCallback(() => {
    void refreshContextQuietly();
  }, [refreshContextQuietly]);

  // Handle opening template dialog (add button click)
  const handleOpenTemplateDialog = useCallback(() => {
    console.log('[ArmillaryView] Add icon clicked - opening template dialog');
    setTemplateDialogVisible(true);
  }, []);

  // Handle hiding template dialog
  const handleHideTemplateDialog = useCallback(() => {
    setTemplateDialogVisible(false);
    setSelectedSphereType(null);
  }, []);

  // Handle sphere type selection in template dialog
  const handleSphereTypeSelect = useCallback((sphereType: string, source: 'type' | 'usecase', label: string) => {
    console.log('[ArmillaryView] Sphere type selected:', sphereType, source, label);
    setSelectedSphereType({ source, label });
  }, []);

  // Handle template selection (use template button)
  const handleUseTemplate = useCallback((schema: any) => {
    console.log('[ArmillaryView] Template selected:', schema);
    setSelectedTemplateSchema(schema);
    setAddSphereDialogVisible(true);
    setTemplateDialogVisible(false);
  }, []);

  // Handle hiding add sphere dialog
  const handleHideAddSphereDialog = useCallback(() => {
    setAddSphereDialogVisible(false);
    setSelectedTemplateSchema(null);
    setSelectedSphereType(null);
  }, []);

  // Handle saving new sphere
  const handleAddSphereSave = useCallback((newSphere: Sphere, _parentId: string, _ancestorIds?: string[]) => {
    console.log('[ArmillaryView] New sphere created:', newSphere);

    // Add the new sphere to local state so it appears as an orbital
    // Assign a high gravity score so it appears in the innermost orbit
    const sphereWithGravity = {
      ...newSphere,
      gravity: {
        score: 95,
        confidence: 1,
        bucket: 'critical' as const,
        impact: 50,
      },
      gravityBucket: 'critical' as const,
    };

    setLocalSphereData(prev => [sphereWithGravity, ...prev]);

    // Close the dialog
    handleHideAddSphereDialog();
  }, [handleHideAddSphereDialog]);

  // Permission check for sphere editor
  const fetchPermissionListHandler = useCallback(async (targetSphereId: string, nodeLabel: string, permissions?: string[]) => {
    if (!apiClients?.permissionClient) return null;
    try {
      return await fetchPermissionList(apiClients.permissionClient, {
        resourceType: nodeLabel,
        resourceId: targetSphereId,
        permissions,
      });
    } catch (error) {
      console.error('[ArmillaryView] Permission check failed:', error);
      return null;
    }
  }, [apiClients]);

  // Calculate target angles for distributing multiple nodes around the top area
  const calculateTargetAngles = (count: number): number[] => {
    if (count === 1) return [-Math.PI / 2]; // 12 o'clock

    // Distribute around top: 11, 12, 1 o'clock positions
    // Each hour represents π/6 radians (30 degrees)
    const angles: number[] = [];
    const spacing = Math.PI / 6; // 30 degrees between positions

    if (count === 2) {
      angles.push(
        -Math.PI / 2 - spacing / 2, // ~11:30
        -Math.PI / 2 + spacing / 2  // ~12:30
      );
    } else if (count === 3) {
      angles.push(
        -Math.PI / 2 - spacing, // 11 o'clock
        -Math.PI / 2,           // 12 o'clock
        -Math.PI / 2 + spacing  // 1 o'clock
      );
    } else {
      // For more than 3, distribute evenly around top area
      const totalSpan = Math.PI / 3; // 60 degrees total span
      const angleSpacing = totalSpan / (count - 1);
      for (let i = 0; i < count; i++) {
        angles.push(-Math.PI / 2 - totalSpan / 2 + i * angleSpacing);
      }
    }

    return angles;
  };

  const handleNodeClick = async (nodeInfo: SelectedNodeInfo) => {
    // Get the clicked node
    const clickedNode = displayNodes[nodeInfo.orbitIndex]?.[nodeInfo.nodeIndex];

    // Check if this is a notification node
    const isNotificationNode = clickedNode?.id?.startsWith('notification-');

    if (isNotificationNode && clickedNode) {
      console.log('[ArmillaryView] 🔔 Notification node clicked:', clickedNode);

      // Extract notification data from the custom property
      const notificationData = (clickedNode as any)?.notificationData;

      if (notificationData) {
        // Mark notification as read
        if (toolbarContext?.markNotificationAsRead) {
          await toolbarContext.markNotificationAsRead(notificationData.id);
          console.log('[ArmillaryView] Marked notification as read:', notificationData.id);
        }

        // TODO: Optionally open notification details panel or trigger action
        // You could trigger onNotificationsClick to switch to notifications mode
        // or show a popup with notification details
      }

      // Don't rotate orbits for notification clicks, just return
      return;
    }

    // ALWAYS update satellite viewer immediately (don't debounce this)
    if (clickedNode?.satellites && clickedNode.satellites.length > 0) {
      // Calculate source position for animation
      // Get the center of the orbital visualization as approximate source
      if (mainContainerRef.current) {
        const rect = mainContainerRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        // For more accuracy, we could calculate based on orbit radius and angle
        // But center is good enough for a nice effect
        setSatelliteSourcePosition({ x: centerX, y: centerY });
      }

      setSelectedNodeForSatellites(clickedNode);
      setSatelliteViewerVisible(true);
    } else {
      // Hide satellite viewer if node has no satellites
      setSatelliteViewerVisible(false);
      setSelectedNodeForSatellites(null);
      setSatelliteSourcePosition(undefined);
    }

    // Debounce ONLY the orbit rotation logic (not satellite viewer)
    if (isProcessingClick.current) {
      console.log('[ArmillaryView] ⏳ Orbit rotation debounced - still processing previous rotation');
      return;
    }

    isProcessingClick.current = true;
    console.log('[ArmillaryView] ✅ Processing orbit rotation...');

    setSelectedOrbits(prevSelected => {
      const newSelectedOrbits = new Set(prevSelected);

      // Toggle orbit selection
      if (newSelectedOrbits.has(nodeInfo.orbitIndex)) {
        // Deselect this orbit
        newSelectedOrbits.delete(nodeInfo.orbitIndex);
        console.log('[ArmillaryView] 🔵 Orbit deselected:', nodeInfo.orbitIndex);
      } else {
        // Check selection limit before adding
        if (newSelectedOrbits.size >= MAX_SELECTED_ORBITS) {
          console.log('[ArmillaryView] ⚠️ Max selections reached, ignoring click');
          isProcessingClick.current = false; // Reset flag since no animation will occur
          return prevSelected; // Don't add new orbit
        }
        // Add this orbit to selection
        newSelectedOrbits.add(nodeInfo.orbitIndex);
        console.log('[ArmillaryView] 🟢 Orbit selected:', nodeInfo.orbitIndex);
      }
      console.log('[ArmillaryView] 📊 Selected orbits:', Array.from(newSelectedOrbits));

      // Update node selected state
      setIsNodeSelected(newSelectedOrbits.size > 0);

      // Small delay to let state update take effect, then rotate nodes
      setTimeout(async () => {
        if (newSelectedOrbits.size > 0 && orbitalVisualizationRef.current) {
          // Calculate target angles for all selected orbits
          const targetAngles = calculateTargetAngles(newSelectedOrbits.size);
          const sortedOrbits = Array.from(newSelectedOrbits).sort((a, b) => a - b);

          // Capture ref for type safety in async operations
          const visualizationRef = orbitalVisualizationRef.current;

          // Start ALL rotations in parallel for better performance
          const rotationPromises = sortedOrbits.map(async (orbitIndex, i) => {
            const targetAngle = targetAngles[i];
            // Rotate the first node (index 0) of each orbit to the target angle
            const position = await visualizationRef.rotateToNode(orbitIndex, 0, targetAngle);
            return { orbitIndex, position };
          });

          // Wait for all rotations to complete
          const results = await Promise.all(rotationPromises);

          // Build positions map from results
          const newPositions = new Map<number, NodePosition>();
          results.forEach(({ orbitIndex, position }) => {
            if (position) {
              newPositions.set(orbitIndex, position);
            }
          });
          setNodePositions(newPositions);
        } else {
          // No orbits selected, clear all positions
          setNodePositions(new Map());
        }

        // Reset processing flag after animations complete (1000ms rotation + buffer)
        setTimeout(() => {
          isProcessingClick.current = false;
        }, 1100);
      }, 10);

      return newSelectedOrbits;
    });
  };

  const handleCloseSelection = async (orbitIndexToRemove?: number) => {
    // Debounce: prevent rapid clicks from causing state conflicts
    if (isProcessingClick.current) {
      return;
    }

    isProcessingClick.current = true;

    if (orbitIndexToRemove === undefined) {
      // Remove all orbits
      setSelectedOrbits(new Set());
      setIsNodeSelected(false);
      setNodePositions(new Map());
      isProcessingClick.current = false; // Reset immediately for "remove all"
    } else {
      // Remove specific orbit
      setSelectedOrbits(prevSelected => {
        const newSelectedOrbits = new Set(prevSelected);
        newSelectedOrbits.delete(orbitIndexToRemove);

        setIsNodeSelected(newSelectedOrbits.size > 0);

        // Small delay to let state update take effect, then recalculate positions
        setTimeout(async () => {
          if (newSelectedOrbits.size > 0 && orbitalVisualizationRef.current) {
            // Recalculate positions for remaining orbits
            const targetAngles = calculateTargetAngles(newSelectedOrbits.size);
            const sortedOrbits = Array.from(newSelectedOrbits).sort((a, b) => a - b);

            // Capture ref for type safety in async operations
            const visualizationRef = orbitalVisualizationRef.current;

            // Start ALL rotations in parallel for better performance
            const rotationPromises = sortedOrbits.map(async (orbitIndex, i) => {
              const targetAngle = targetAngles[i];
              const position = await visualizationRef.rotateToNode(orbitIndex, 0, targetAngle);
              return { orbitIndex, position };
            });

            // Wait for all rotations to complete
            const results = await Promise.all(rotationPromises);

            // Build positions map from results
            const newPositions = new Map<number, NodePosition>();
            results.forEach(({ orbitIndex, position }) => {
              if (position) {
                newPositions.set(orbitIndex, position);
              }
            });
            setNodePositions(newPositions);
          } else {
            // No orbits left, clear positions
            setNodePositions(new Map());
          }

          // Reset processing flag after animations complete (1000ms rotation + buffer)
          setTimeout(() => {
            isProcessingClick.current = false;
          }, 1100);
        }, 10);

        return newSelectedOrbits;
      });
    }
  };

  const handleCenterClick = useCallback(() => {
    // Find the first selected orbital's sphere info
    const sortedOrbits = Array.from(selectedOrbits).sort((a, b) => a - b);
    if (sortedOrbits.length === 0) return;

    const orbitIndex = sortedOrbits[0];
    const node = displayNodes[orbitIndex]?.[0];
    if (!node) return;

    const sphereNode = node.sphereNode;
    const newSphereId = sphereNode?.id ?? node.id;
    const newType = (sphereNode?.properties?.type as string) || sphereNode?.label || null;

    // Push current sphere info onto breadcrumb ancestors
    const currentInfo = activeSphereInfo || (rootNode ? {
      sphereId: rootNode.id,
      name: (rootNode.properties?.name as string) || 'Sphere',
      glyph: rootNode.glyph,
      glyphColor: rootNode.glyphColor,
      imageId: rootNode.properties?.imageId as string | undefined,
    } : null);

    if (currentInfo) {
      setBreadcrumbs(prev => [...prev, currentInfo]);
    }

    // Set the new sphere's info immediately from node data
    setActiveSphereInfo({
      sphereId: newSphereId,
      name: (sphereNode?.properties?.name as string) || node.label || 'Sphere',
      glyph: sphereNode?.glyph,
      glyphColor: sphereNode?.glyphColor,
      imageId: sphereNode?.properties?.imageId as string | undefined,
    });

    // Update internal state — triggers useContextTree refetch
    setActiveSphereId(newSphereId);

    // Publish to context
    if (armillaryContext) {
      armillaryContext.setRootSphereId(newSphereId);
      armillaryContext.setRootSphereType(newType);
    }

    // Notify toolbar of sphere change
    emitSphereCentered(newSphereId, newType || undefined);

    // Reset selection state (same pattern as handleNodeDoubleClick)
    setSelectedOrbits(new Set());
    setIsNodeSelected(false);
    setSatelliteViewerVisible(false);
    // Show loader for navigation
    setShowLoader(true);
    setLoaderFadingOut(false);
  }, [selectedOrbits, displayNodes, activeSphereInfo, rootNode, armillaryContext, emitSphereCentered]);

  // Emit toolbar expand to chatbox when conversation icon is clicked
  const handleConversationClick = useCallback(() => {
    emitToolbarExpand('chatbox');
  }, [emitToolbarExpand]);

  // Open toolbar to notifications tool when bell icon is clicked
  const handleNotificationsClick = useCallback(() => {
    emitToolbarExpand('notifications');
  }, [emitToolbarExpand]);

  // Open the toolbar's status tool for the centered sphere when the ring's
  // status icon is clicked. Only wired when a toolbar is present (see the
  // gated prop below); otherwise the molecule falls back to its inline popover.
  const handleStatusToolClick = useCallback(() => {
    if (activeSphereId) {
      emitToolbarExpand('status', activeSphereId);
    }
  }, [emitToolbarExpand, activeSphereId]);

  // Open toolbar to tasks tool when dial cycles to the 'tasks' dial
  const handleDialChange = useCallback((dialType: string) => {
    if (dialType === 'tasks') {
      emitToolbarExpand('tasks');
    }
  }, [emitToolbarExpand]);

  // Handle double-click navigation into an orbital sphere
  const handleNodeDoubleClick = useCallback((nodeInfo: SelectedNodeInfo) => {
    const node = displayNodes[nodeInfo.orbitIndex]?.[nodeInfo.nodeIndex];
    if (!node) return;

    const sphereNode = node.sphereNode;
    const newSphereId = sphereNode?.id ?? node.id;

    // Push current sphere info onto breadcrumb ancestors
    const currentInfo = activeSphereInfo || (rootNode ? {
      sphereId: rootNode.id,
      name: (rootNode.properties?.name as string) || 'Sphere',
      glyph: rootNode.glyph,
      glyphColor: rootNode.glyphColor,
      imageId: rootNode.properties?.imageId as string | undefined,
    } : null);

    if (currentInfo) {
      setBreadcrumbs(prev => [...prev, currentInfo]);
    }

    // Set the new sphere's info immediately from node data (no fetch wait)
    setActiveSphereInfo({
      sphereId: newSphereId,
      name: (sphereNode?.properties?.name as string) || node.label || 'Sphere',
      glyph: sphereNode?.glyph,
      glyphColor: sphereNode?.glyphColor,
      imageId: sphereNode?.properties?.imageId as string | undefined,
    });

    // Update internal state — triggers useContextTree refetch
    setActiveSphereId(newSphereId);

    const newType = (sphereNode?.properties?.type as string) || sphereNode?.label || null;

    // Publish to context — triggers notification/task refetch
    if (armillaryContext) {
      armillaryContext.setRootSphereId(newSphereId);
      armillaryContext.setRootSphereType(newType);
    }

    // Notify toolbar of sphere change
    emitSphereCentered(newSphereId, newType || undefined);

    // Reset orbital selection state
    setSelectedOrbits(new Set());
    setIsNodeSelected(false);
    setSatelliteViewerVisible(false);
    // Show loader for navigation
    setShowLoader(true);
    setLoaderFadingOut(false);
  }, [displayNodes, armillaryContext, activeSphereInfo, rootNode, emitSphereCentered]);

  // Handle breadcrumb click — navigate back to a previous sphere
  const handleBreadcrumbClick = useCallback((index: number) => {
    const entry = breadcrumbs[index];
    if (!entry) return;

    // Trim breadcrumbs to everything before the clicked entry
    setBreadcrumbs(prev => prev.slice(0, index));

    // Set this entry as current immediately (no fetch wait)
    setActiveSphereInfo(entry);

    // Navigate to that sphere
    setActiveSphereId(entry.sphereId);

    if (armillaryContext) {
      armillaryContext.setRootSphereId(entry.sphereId);
    }

    // Notify toolbar of sphere change
    emitSphereCentered(entry.sphereId);

    // Reset orbital selection state
    setSelectedOrbits(new Set());
    setIsNodeSelected(false);
    setSatelliteViewerVisible(false);
    // Show loader for navigation
    setShowLoader(true);
    setLoaderFadingOut(false);
  }, [breadcrumbs, armillaryContext, emitSphereCentered]);

  // Satellite viewer handlers
  const handleSatelliteViewerHide = useCallback(() => {
    setSatelliteViewerVisible(false);
    setSelectedNodeForSatellites(null);
    setSatelliteSourcePosition(undefined);
  }, []);

  const handleSatelliteDoubleClick = useCallback((satellite: SatelliteSphere) => {
    const newSphereId = satellite.id;

    // Push current sphere info onto breadcrumb ancestors
    const currentInfo = activeSphereInfo || (rootNode ? {
      sphereId: rootNode.id,
      name: (rootNode.properties?.name as string) || 'Sphere',
      glyph: rootNode.glyph,
      glyphColor: rootNode.glyphColor,
      imageId: rootNode.properties?.imageId as string | undefined,
    } : null);

    if (currentInfo) {
      setBreadcrumbs(prev => [...prev, currentInfo]);
    }

    // Set the new sphere's info immediately from satellite data (no fetch wait)
    setActiveSphereInfo({
      sphereId: newSphereId,
      name: satellite.name,
      glyph: satellite.glyph,
      glyphColor: satellite.glyphColor,
    });

    // Update internal state — triggers useContextTree refetch
    setActiveSphereId(newSphereId);

    // Publish to context — triggers notification/task refetch
    if (armillaryContext) {
      armillaryContext.setRootSphereId(newSphereId);
      armillaryContext.setRootSphereType(null);
    }

    // Notify toolbar of sphere change
    emitSphereCentered(newSphereId);

    // Reset orbital selection state
    setSelectedOrbits(new Set());
    setIsNodeSelected(false);
    setSatelliteViewerVisible(false);
    setSelectedNodeForSatellites(null);
    setSatelliteSourcePosition(undefined);
    // Show loader for navigation
    setShowLoader(true);
    setLoaderFadingOut(false);
  }, [armillaryContext, activeSphereInfo, rootNode, emitSphereCentered]);

  // Memoize close handlers to prevent recreating functions on every render
  const closeHandlers = useMemo(() => {
    const handlers = new Map<number, () => void>();
    nodePositions.forEach((_, orbitIndex) => {
      handlers.set(orbitIndex, () => handleCloseSelection(orbitIndex));
    });
    return handlers;
  }, [nodePositions]);

  const templateDialogHeader = (() => {
    if (!selectedSphereType) return 'Add Template';
    const selectionLabel = selectedSphereType.source === 'type' ? 'Type Selected' : 'Use Case Selected';
    return `${selectionLabel} (${selectedSphereType.label})`;
  })();

  return (
    <div className={`armillary-view-organism ${className}`}>
      {/* Orbital Spinner Loader */}
      {showLoader && (
        <LoaderMolecule
          caption={t('armillary.loading', 'Loading spheres...')}
          className={loaderFadingOut ? 'loader-molecule--fade-out' : ''}
        />
      )}

      <div className="armillary-view-content" ref={mainContainerRef}>
        {/* Header with breadcrumb navigation */}
        <div className="armillary-view-header">
          <nav className="armillary-breadcrumbs" aria-label="Sphere navigation">
            <ol className="armillary-breadcrumbs__list">
              {breadcrumbs.map((entry, index) => (
                <React.Fragment key={entry.sphereId}>
                  <li className="armillary-breadcrumbs__item">
                    <button
                      className="armillary-breadcrumbs__link"
                      onClick={() => handleBreadcrumbClick(index)}
                      title={`Navigate to ${entry.name}`}
                    >
                      <SphereAvatar
                        glyph={entry.glyph}
                        glyphColor={entry.glyphColor}
                        sphereId={entry.sphereId}
                        sphereName={entry.name}
                        imageId={entry.imageId}
                        size="small"
                        status={undefined}
                        decorative
                      />
                      <span>{entry.name}</span>
                    </button>
                  </li>
                  <li className="armillary-breadcrumbs__separator" aria-hidden="true">
                    <i className="pi pi-angle-right" />
                  </li>
                </React.Fragment>
              ))}
              {(activeSphereInfo || rootNode) && (
                <li className="armillary-breadcrumbs__item">
                  <span className="armillary-breadcrumbs__current">
                    <SphereAvatar
                      glyph={activeSphereInfo?.glyph ?? rootNode?.glyph}
                      glyphColor={activeSphereInfo?.glyphColor ?? rootNode?.glyphColor}
                      sphereId={activeSphereInfo?.sphereId ?? rootNode?.id}
                      sphereName={activeSphereInfo?.name ?? (rootNode?.properties?.name as string)}
                      imageId={activeSphereInfo?.imageId ?? (rootNode?.properties?.imageId as string)}
                      size="small"
                      status={undefined}
                      decorative
                    />
                    <span>{activeSphereInfo?.name ?? (rootNode?.properties?.name as string) ?? 'Sphere'}</span>
                  </span>
                </li>
              )}
              {activeContextSystemName !== ARMILLARY_CONTEXT && (
                <li className="armillary-breadcrumbs__item">
                  <span className="armillary-breadcrumbs__context-label">
                    {activeContextDisplayName}
                  </span>
                </li>
              )}
            </ol>
          </nav>
          {showControls && (
            <div className="armillary-view-header__controls">
              <ControlPanelMolecule
                isPlaying={isAnimating}
                onPlay={handlePlay}
                onPause={handlePause}
                onStop={handleStop}
                showStop={true}
              />
            </div>
          )}
        </div>
        <div className={`armillary-orbital-container${isFadingOut ? ' armillary-orbital-container--fading' : ''}`}>
          {isNodeSelected && Array.from(nodePositions.entries()).map(([orbitIndex, position]) => (
            <SelectedNodeHeaderMolecule
              key={orbitIndex}
              onCenter={handleCenterClick}
              onClose={closeHandlers.get(orbitIndex)!}
              nodePosition={position}
            />
          ))}
          <OrbitalVisualizationMolecule
            ref={orbitalVisualizationRef}
            orbits={activeOrbits}
            nodes={displayNodes}
            dialType="avatar"
            sphere={rootSphereCompat}
            isAnimating={isAnimating}
            centerImagePath={centerImagePath}
            onNodeClick={handleNodeClick}
            selectedOrbits={selectedOrbits}
            containerRef={mainContainerRef}
            useVelocitySpeed={useVelocitySpeed}
            onRefresh={handleRefresh}
            onConversationClick={handleConversationClick}
            onNotificationsClick={handleNotificationsClick}
            onStatusToolClick={toolbarContext ? handleStatusToolClick : undefined}
            onEditClick={handleOpenSphereEditor}
            onAddClick={handleOpenTemplateDialog}
            onNodeDoubleClick={handleNodeDoubleClick}
            onDialChange={handleDialChange}
            onContextSelect={handleContextSelect}
            activeContextSystemName={activeContextSystemName}
            onContextFilterClick={handleContextFilterClick}
            onAnimationSettled={() => setIsAnimating(false)}
          />
        </div>
      </div>

      {/* Sphere Editor Dialog */}
      <DialogWrapper
        header="Edit Sphere"
        visible={editSphereDialogVisible}
        onHide={handleCloseSphereEditor}
      >
        {sphereToEdit && (
          <EditSphere
            sphere={sphereToEdit}
            parentSphere={sphereToEdit}
            onSave={handleSaveSphere}
            onSphereRefresh={handleSphereRefresh}
            onClose={handleCloseSphereEditor}
            fetchPermissionList={fetchPermissionListHandler}
          />
        )}
      </DialogWrapper>

      {/* Template Selection Dialog */}
      <DialogWrapper
        header={templateDialogHeader}
        visible={templateDialogVisible}
        onHide={handleHideTemplateDialog}
        className="add-template-dialog"
        style={{ width: '85vw', maxWidth: '1600px', height: '80vh' }}
      >
        <AddTemplate
          onSphereTypeSelect={handleSphereTypeSelect}
          // sphereId drives useAvailableTypes — without it the dialog has no schemas
          // (and no resolved node_label), so the grid is always empty.
          sphereId={rootNode?.id}
          assigneeSystemName={(rootNode?.properties?.type as string) ?? ''}
          // Explicit override; otherwise the hook resolves the sphere's node_label
          // (org-independent) so eligibility works even when useSchemaType can't.
          assigneeNodeLabel={(rootNode?.label as string) || undefined}
          onUseTemplate={handleUseTemplate}
          showPreviewButton={true}
          propertyFilters={[
            { property: 'node_label', operator: 'equals', value: 'sphere' }
          ]}
        />
      </DialogWrapper>

      {/* Add Sphere Dialog */}
      <DialogWrapper
        header="Add Sphere"
        visible={addSphereDialogVisible}
        onHide={handleHideAddSphereDialog}
        className="add-sphere-dialog"
      >
        {rootSphereCompat && selectedTemplateSchema && (
          <AddSphere
            parentSphere={rootSphereCompat}
            schema={selectedTemplateSchema}
            onSave={handleAddSphereSave}
            onClose={handleHideAddSphereDialog}
          />
        )}
      </DialogWrapper>

      {/* Satellite Viewer Panel */}
      <SatelliteViewerMolecule
        visible={satelliteViewerVisible}
        onHide={handleSatelliteViewerHide}
        parentNode={selectedNodeForSatellites}
        onSatelliteDoubleClick={handleSatelliteDoubleClick}
        sourcePosition={satelliteSourcePosition}
      />

      {/* Context Switcher Toolbar (richer dropdown with views) */}
      <ContextSwitcherToolbarMolecule
        ref={contextSwitcherRef}
        hideChip
        activeContextSystemName={activeContextSystemName}
        activeContextDisplayName={activeContextDisplayName}
        defaultContextSystemName={ARMILLARY_CONTEXT}
        activeViewSystemName={activeViewSystemName}
        viewType="atlas"
        sphereId={activeSphereId ?? null}
        rootLabel={activeSphereInfo?.name ?? (rootNode?.properties?.name as string) ?? ''}
        canEditActiveContext={false}
        isDirty={false}
        dirtyKinds={[]}
        onContextSelect={handleContextSelect}
        onViewSelect={handleViewSelect}
        onNewView={handleNewView}
        onEditView={handleEditView}
        onEditContext={onEditContext}
        switcherRefreshKey={switcherRefreshKey}
      />

      {/* Context View Editor Overlay */}
      {viewEditorState && (
        <ContextViewEditorOverlayOrganism
          mode={viewEditorState.mode}
          contextSystemName={viewEditorState.contextSystemName}
          contextDisplayName={viewEditorState.contextDisplayName}
          viewSystemName={viewEditorState.viewSystemName}
          onSaved={handleViewSaved}
          onDeleted={handleViewDeleted}
          onClose={() => setViewEditorState(null)}
        />
      )}
    </div>
  );
};

export default ArmillaryViewOrganism;
